import { fmtDate, todayStr } from "./format";
import { isHourlyJob } from "./hourly";

/**
 * Roadway Buddy (Asana 1219929429708587, design of 2026-10-29): a coordinator's
 * time off, the buddy covering it, and what is due on their jobs meanwhile.
 * Requests come from the backend ({id, mcId, mc, buddyId, buddy, start, end,
 * status, reason, jobs: [{leadId, buddyId, buddy, note, clientName, zohoId,
 * moveType, price, source, due}], managerNote, decidedBy}).
 */

/** A buddy covering more jobs than this in one window gets a warning for the manager. */
export const BUDDY_MAX_JOBS = 6;

/** Everything due on a job between start and end, oldest first: what the buddy has to handle. */
export const dueItems = (job, start, end) => {
  if (!job || job.postMoveClosed || !start || !end) return [];
  const out = [];
  const add = (date, label) => {
    if (date && date >= start && date <= end) out.push({ date, label });
  };
  const n = job.s2Notes || {};
  add(job.moveDate, "Move day");
  add(job.packingDate, "Packing day");
  add(job.loadingDate, "Loading day");
  add(job.unpackingDate, "Unpacking day");
  add(job.callbackDate, "Scheduled callback");
  add(n.sitConfirmedDate, "SIT Local delivery");
  add(n.sitOpsCheckDate, "SIT Local Ops check-in");
  add((job.serviceDates || {}).localDeliveryDate, "Local delivery");
  add((job.driverDelivery || {}).etaDate, "Driver delivery ETA");
  add(job.deliveryWindowStart, "Delivery window opens");
  add(job.deliveryWindowEnd, "Delivery window ends");
  (job.reminders || [])
    .filter((r) => !r.completed && r.kind !== "pm-issue")
    .forEach((r) => add(r.date, "Reminder: " + r.text));
  const seen = {};
  return out
    .filter((o) => {
      const k = o.date + o.label;
      if (seen[k]) return false;
      seen[k] = 1;
      return true;
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

export const overlaps = (r, start, end) => r.start <= end && r.end >= start;

/** Who covers this job: its own buddy, else the main one. */
export const buddyIdFor = (r, leadId) => {
  const j = (r.jobs || []).find((x) => Number(x.leadId) === Number(leadId));
  return (j && j.buddyId) || r.buddyId;
};

export const buddyNameFor = (r, leadId) => {
  const j = (r.jobs || []).find((x) => Number(x.leadId) === Number(leadId));
  return (j && j.buddyId && j.buddy) || r.buddy;
};

/** Is this coordinator out (approved) at any point between start and end? */
export const isOut = (requests, mcId, start, end, exceptId) =>
  (requests || []).find(
    (o) => o.id !== exceptId && o.mcId === mcId && o.status === "approved" && overlaps(o, start, end),
  ) || null;

/** The jobs of this MC with something due in the window: [{job, items}]. */
export const myDueJobs = (jobs, ownerZohoId, start, end) =>
  (jobs || [])
    .filter((j) => !ownerZohoId || !j.ownerZohoId || j.ownerZohoId === ownerZohoId)
    .map((job) => ({ job, items: dueItems(job, start, end) }))
    .filter((x) => x.items.length);

/** Why the request cannot be sent, or "". */
export const requestError = (draft, due, today = todayStr()) => {
  if (!draft.start || !draft.end || draft.end < draft.start) return "Pick valid dates.";
  if (draft.start < today) return "The first day out cannot be in the past.";
  if (!draft.buddyId) return "Choose a buddy.";
  const missing = due.filter(({ job }) => !(draft.notes[job.id] || "").trim());
  return missing.length ? "Add a handoff note for: " + missing.map(({ job }) => job.clientName).join(", ") + "." : "";
};

/** A job with storage: flagged on the lead, tagged, or sold as a service. */
export const hasStorage = (job) =>
  /^yes/i.test(String(job.storageRequired || "")) ||
  (job.tags || []).some((t) => /storage/i.test(t)) ||
  (job.additionalServices || []).some((s) => /storage/i.test(s));

/** The body POSTed to /time-off from the draft. */
export const requestBody = (draft, due) => ({
  start: draft.start,
  end: draft.end,
  buddyId: draft.buddyId,
  reason: (draft.reason || "").trim(),
  jobs: due.map(({ job, items }) => ({
    leadId: job.leadId,
    buddyId: draft.jobBuddy[job.id] && draft.jobBuddy[job.id] !== draft.buddyId ? draft.jobBuddy[job.id] : null,
    note: (draft.notes[job.id] || "").trim(),
    clientName: job.clientName,
    zohoId: job.zohoId,
    moveType: job.moveType,
    price: job.price,
    source: job.source || "",
    hourly: isHourlyJob(job),
    storage: hasStorage(job),
    due: items,
  })),
});

/**
 * What the manager should know before approving: a buddy who is out too, or
 * one who would cover more than BUDDY_MAX_JOBS jobs across every absence in
 * the window.
 */
export const approvalWarnings = (r, requests) => {
  const byBuddy = {};
  (r.jobs || []).forEach((j) => {
    const id = j.buddyId || r.buddyId;
    byBuddy[id] = byBuddy[id] || { name: (j.buddyId && j.buddy) || r.buddy, n: 0 };
    byBuddy[id].n += 1;
  });
  const others = (requests || []).filter((x) => x.status === "approved" && x.id !== r.id && overlaps(x, r.start, r.end));
  const out = [];
  Object.keys(byBuddy).forEach((id) => {
    const { name, n } = byBuddy[id];
    const busy = others.find((o) => String(o.mcId) === String(id));
    if (busy) out.push({ cls: "danger", text: `${name} is also out ${rangeLabel(busy)} — they cannot cover.` });
    const more = others.reduce(
      (sum, x) => sum + (x.jobs || []).filter((j) => String(j.buddyId || x.buddyId) === String(id)).length,
      0,
    );
    if (n + more > BUDDY_MAX_JOBS)
      out.push({ cls: "warning", text: `${name} would cover ${n + more} jobs in this window (limit ${BUDDY_MAX_JOBS}).` });
  });
  return out;
};

const short = (d) => fmtDate(d).replace(/, \d{4}/, "");
export const rangeLabel = (r) => short(r.start) + " – " + short(r.end);

/**
 * The cover tag on a job card: an approved absence of its owner that has not
 * ended and has this job in it. {label, mine} or null.
 */
export const coverTag = (job, requests, meId, today = todayStr()) => {
  const r = (requests || []).find(
    (x) =>
      x.status === "approved" &&
      x.end >= today &&
      (x.jobs || []).some((j) => Number(j.leadId) === Number(job.leadId)),
  );
  if (!r) return null;
  const buddyId = buddyIdFor(r, job.leadId);
  const mine = buddyId === meId;
  const first = (s) => (s || "").split(" ")[0];
  const who = mine ? "You cover for " + first(r.mc) : "Covered by " + first(buddyNameFor(r, job.leadId));
  return { mine, label: `${who} · ${short(r.start)}–${short(r.end)}${r.start <= today ? "" : " (upcoming)"}` };
};
